import { useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Stack,
  TextField, Button, Divider, Alert, Chip,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTessieSummary, useTessieConfig } from '../../hooks/useTessie';
import { useRoster } from '../../hooks/useRoster';

function fmtCurrency(n) {
  return `$${parseFloat(n || 0).toFixed(2)}`;
}
import { toLocalDateStr } from '../../lib/utils';

function fmtMiles(n) {
  return `${parseFloat(n || 0).toFixed(1)} mi`;
}

export default function MileageSummary() {
  const today = toLocalDateStr(new Date());
  const thirtyAgo = toLocalDateStr(new Date(Date.now() - 30 * 86400000));
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);

  const { data: summaryData, isLoading } = useTessieSummary({
    from: dateFrom || undefined,
    to: dateTo ? `${dateTo}T23:59:59` : undefined,
  });
  const { data: config } = useTessieConfig();
  const { data: roster = [] } = useRoster();

  const rosterMap = useMemo(() => {
    const m = {};
    roster.forEach(r => { m[r.id] = r; });
    return m;
  }, [roster]);

  // Aggregate summary rows by roster_id
  const personSummary = useMemo(() => {
    if (!summaryData?.summary) return [];
    const byPerson = {};
    for (const row of summaryData.summary) {
      const key = row.roster_id || '__unassigned__';
      if (!byPerson[key]) {
        byPerson[key] = { rosterId: row.roster_id, businessMiles: 0, personalMiles: 0, businessDrives: 0, personalDrives: 0, personalCharges: 0 };
      }
      const miles = parseFloat(row.total_miles || 0);
      const drives = parseInt(row.drive_count || 0);
      const charges = parseFloat(row.personal_charges || 0);
      if (row.tag === 'business') {
        byPerson[key].businessMiles += miles;
        byPerson[key].businessDrives += drives;
      } else if (row.tag === 'personal') {
        byPerson[key].personalMiles += miles;
        byPerson[key].personalDrives += drives;
        byPerson[key].personalCharges += charges;
      }
    }
    return Object.values(byPerson).sort((a, b) => b.personalCharges - a.personalCharges);
  }, [summaryData]);

  const totals = useMemo(() => {
    return personSummary.reduce((acc, p) => ({
      businessMiles: acc.businessMiles + p.businessMiles,
      personalMiles: acc.personalMiles + p.personalMiles,
      personalCharges: acc.personalCharges + p.personalCharges,
      totalDrives: acc.totalDrives + p.businessDrives + p.personalDrives,
    }), { businessMiles: 0, personalMiles: 0, personalCharges: 0, totalDrives: 0 });
  }, [personSummary]);

  const handleExportCSV = () => {
    const rows = [['Name', 'Business Miles', 'Personal Miles', 'Personal Charges', 'Rate']];
    personSummary.forEach(p => {
      const name = p.rosterId ? (rosterMap[p.rosterId]?.name || p.rosterId) : 'Unassigned';
      rows.push([name, p.businessMiles.toFixed(1), p.personalMiles.toFixed(1), p.personalCharges.toFixed(2), config?.mileageRate || 0.75]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mileage-summary-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Date range + export */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          type="date" label="From" size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          type="date" label="To" size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          sx={{ width: 150 }}
        />
        <Box sx={{ flex: 1 }} />
        <Chip
          label={`Rate: ${fmtCurrency(config?.mileageRate || 0.75)}/mi`}
          size="small" variant="outlined"
        />
        <Button
          size="small" variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={!personSummary.length}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Unconfirmed warning */}
      {summaryData?.unconfirmed?.count > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {summaryData.unconfirmed.count} unconfirmed drives ({fmtMiles(summaryData.unconfirmed.miles)}) not included in summary. Review in Drive Log tab.
        </Alert>
      )}

      {isLoading ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Loading summary...</Typography>
      ) : personSummary.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No confirmed drives in this period</Typography>
      ) : (
        <>
          {/* Per-person cards */}
          <Stack spacing={2}>
            {personSummary.map((p, i) => {
              const member = p.rosterId ? rosterMap[p.rosterId] : null;
              const name = member?.name || 'Unassigned';
              const totalMiles = p.businessMiles + p.personalMiles;
              return (
                <Card key={p.rosterId || i} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        {member?.accentColor && (
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: member.accentColor }} />
                        )}
                        <Typography variant="subtitle1" fontWeight={600}>{name}</Typography>
                      </Stack>
                      {p.personalCharges > 0 && (
                        <Typography variant="subtitle1" fontWeight={700} color="error.main">
                          {fmtCurrency(p.personalCharges)} owed
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', columnGap: 3, rowGap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="body2" fontWeight={500}>{fmtMiles(totalMiles)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="success.main">Business</Typography>
                        <Typography variant="body2">{fmtMiles(p.businessMiles)} ({p.businessDrives} trips)</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="error.main">Personal</Typography>
                        <Typography variant="body2">{fmtMiles(p.personalMiles)} ({p.personalDrives} trips)</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          {/* Company totals */}
          <Divider sx={{ my: 2 }} />
          <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <CarIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Company Total</Typography>
                </Stack>
                <Typography variant="subtitle1" fontWeight={700} color="error.main">
                  {fmtCurrency(totals.personalCharges)} personal charges
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', columnGap: 3, rowGap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Confirmed</Typography>
                  <Typography variant="body2" fontWeight={500}>{fmtMiles(totals.businessMiles + totals.personalMiles)} ({totals.totalDrives} trips)</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="success.main">Business</Typography>
                  <Typography variant="body2">{fmtMiles(totals.businessMiles)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="error.main">Personal</Typography>
                  <Typography variant="body2">{fmtMiles(totals.personalMiles)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
